import React, { useEffect, useState } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

export default function MasterList({ title, listPath, itemPath, onReorder }) {
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");

  const refresh = () =>
    api.get(listPath).then((r) => setItems(r.data)).catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [listPath]);

  const add = async () => {
    if (!label.trim()) return;
    try {
      await api.post(listPath, { label: label.trim() });
      setLabel("");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (it) => {
    try {
      await api.patch(itemPath(it.id), { active: !it.active });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const del = async (it) => {
    try {
      await api.delete(itemPath(it.id));
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems(next);
    try {
      await onReorder(next.map((x) => x.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reorder");
      refresh();
    }
  };

  return (
    <div className="surface-card p-4">
      <div className="font-medium text-slate-800">{title}</div>
      <div className="mt-3 flex gap-2">
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={`Add ${title.toLowerCase()}…`}
          onKeyDown={(e) => e.key === "Enter" && add()}
        />
        <Button onClick={add}>Add</Button>
      </div>
      <div className="mt-3 divide-y divide-slate-100">
        {items.map((it, idx) => (
          <div key={it.id} className="py-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="flex flex-col">
                <button
                  className="text-slate-300 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-300"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  aria-label="Move up"
                  title="Move up"
                >
                  <CaretUp size={12} weight="bold" />
                </button>
                <button
                  className="text-slate-300 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-300"
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1}
                  aria-label="Move down"
                  title="Move down"
                >
                  <CaretDown size={12} weight="bold" />
                </button>
              </div>
              <span className={`truncate ${it.active ? "text-slate-800" : "text-slate-400 line-through"}`}>
                {it.label}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                className="text-xs text-slate-500 hover:text-slate-900"
                onClick={() => toggle(it)}
              >
                {it.active ? "Disable" : "Enable"}
              </button>
              <button
                className="text-xs text-rose-500 hover:text-rose-700"
                onClick={() => del(it)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="py-3 text-xs text-slate-400">Nothing yet.</div>
        )}
      </div>
    </div>
  );
}
